import * as React from "react";

type Stage =
  | "loading"
  | "selecting"
  | "analysing"
  | "reviewing"
  | "ready-to-submit"
  | "submitting"
  | "submitted";
type CandidateKind = "email" | "attachment";
type CandidateStatus = "not-selected" | "needs-review" | "ready" | "submitted";

type AttachmentInfo = {
  name: string;
};

type EmailInfo = {
  id: string;
  subject: string;
  attachments: AttachmentInfo[];
};

type MetadataField = {
  key: string;
  label: string;
  value: string;
  required?: boolean;
};

type DocumentCandidate = {
  id: string;
  emailId: string;
  emailSubject: string;
  kind: CandidateKind;
  name: string;
  selected: boolean;
  status: CandidateStatus;
  documentType: string;
  suggestions: string[];
  metadata: MetadataField[];
  note?: string;
};

const documentTypes = [
  "Candidate CV",
  "Right to Work Evidence",
  "Employment Contract",
  "Offer Letter",
  "Change of Role",
  "Absence Note",
  "Disciplinary Record",
  "Training Certificate",
  "Performance Review",
  "Leaver Form",
];

const knownAttachmentsBySubject: Record<string, string[]> = {
  "candidate cv - felix dunmar": ["Felix Dunmar CV.pdf"],
  "please get back in touch!": [],
  "new starter documents - simon penrose": ["Simon Penrose Offer Letter.pdf"],
  "new starter right to work simon penrose": ["Right to Work - Simon Penrose.pdf", "Scanned document.jpg"],
};

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

function isKnownSubject(subject: string, known: string): boolean {
  return normalize(subject).includes(known);
}

function getKnownAttachments(subject: string): AttachmentInfo[] {
  const normalizedSubject = normalize(subject);
  const names = Object.entries(knownAttachmentsBySubject).find(([key]) => normalizedSubject.includes(key))?.[1] || [];

  return names.map((name) => ({ name }));
}

function createFallbackEmail(): EmailInfo {
  return {
    id: "fallback-cv",
    subject: "Candidate CV - Felix Dunmar",
    attachments: [{ name: "Felix Dunmar CV.pdf" }],
  };
}

function getCurrentItemEmail(): EmailInfo | null {
  const item = Office.context.mailbox.item as any;

  if (!item) {
    return null;
  }

  const subject = item.subject || "Selected email";
  const attachments =
    Array.isArray(item.attachments) && item.attachments.length > 0
      ? item.attachments.map((attachment: any) => ({ name: attachment.name || "Attachment" }))
      : getKnownAttachments(subject);

  return {
    id: item.itemId || `current-${subject}`,
    subject,
    attachments,
  };
}

function getSelectedEmails(): Promise<EmailInfo[]> {
  const mailbox = Office.context.mailbox as any;

  if (typeof mailbox.getSelectedItemsAsync !== "function") {
    return Promise.resolve([getCurrentItemEmail() || createFallbackEmail()]);
  }

  return new Promise((resolve) => {
    mailbox.getSelectedItemsAsync((result: Office.AsyncResult<Office.SelectedItemDetails[]>) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded || !result.value?.length) {
        resolve([getCurrentItemEmail() || createFallbackEmail()]);
        return;
      }

      resolve(
        result.value.map((item, index) => {
          const subject = item.subject || `Selected email ${index + 1}`;
          return {
            id: item.itemId || `selected-${index}`,
            subject,
            attachments: getKnownAttachments(subject),
          };
        })
      );
    });
  });
}

function metadata(fields: Array<[string, string, string, boolean?]>): MetadataField[] {
  return fields.map(([key, label, value, required = true]) => ({ key, label, value, required }));
}

function isDateField(field: MetadataField): boolean {
  return field.key.toLowerCase().includes("date") || field.key === "dob";
}

function metadataForDocumentType(documentType: string, candidate: DocumentCandidate): MetadataField[] {
  const lowerName = normalize(candidate.name);
  const lowerSubject = normalize(candidate.emailSubject);

  if (documentType === "Candidate CV") {
    return metadata([
      ["firstName", "First name", lowerName.includes("felix") || lowerSubject.includes("felix") ? "Felix" : ""],
      ["lastName", "Last name", lowerName.includes("felix") || lowerSubject.includes("felix") ? "Dunmar" : ""],
      ["email", "Email address", lowerName.includes("felix") || lowerSubject.includes("felix") ? "felix.dunmar@kessellix.co.uk" : ""],
      ["role", "Role applied for", lowerName.includes("felix") || lowerSubject.includes("felix") ? "HR Coordinator" : ""],
      ["dob", "Date of birth", lowerName.includes("felix") || lowerSubject.includes("felix") ? "1993-05-14" : ""],
    ]);
  }

  if (documentType === "Absence Note") {
    return metadata([
      ["employeeName", "Employee name", lowerSubject.includes("cara") ? "Cara Linton" : ""],
      ["absenceType", "Absence type", lowerSubject.includes("cara") ? "Sickness" : ""],
      ["startDate", "Start date", lowerSubject.includes("cara") ? "2026-06-29" : ""],
      ["endDate", "Expected return date", "", false],
      ["reason", "Reason", lowerSubject.includes("cara") ? "Flu symptoms" : "", false],
    ]);
  }

  if (documentType === "Offer Letter") {
    return metadata([
      ["candidateName", "Candidate name", lowerName.includes("simon") || lowerSubject.includes("simon") ? "Simon Penrose" : ""],
      ["role", "Role offered", lowerName.includes("simon") || lowerSubject.includes("simon") ? "People Operations Advisor" : ""],
      ["salary", "Salary", lowerName.includes("simon") || lowerSubject.includes("simon") ? "£38,000" : ""],
      ["startDate", "Proposed start date", lowerName.includes("simon") || lowerSubject.includes("simon") ? "2026-07-15" : ""],
      ["hiringManager", "Hiring manager", ""],
    ]);
  }

  if (documentType === "Right to Work Evidence") {
    return metadata([
      ["firstName", "First name", lowerName.includes("simon") || lowerSubject.includes("simon") ? "Simon" : ""],
      ["lastName", "Last name", lowerName.includes("simon") || lowerSubject.includes("simon") ? "Penrose" : ""],
      ["documentType", "Document type", lowerName.includes("simon") || lowerSubject.includes("simon") ? "Passport" : ""],
      ["expiryDate", "Expiry date", lowerName.includes("simon") || lowerSubject.includes("simon") ? "2031-06-12" : ""],
      ["verificationDate", "Verification date", ""],
    ]);
  }

  if (documentType === "Change of Role") {
    return metadata([
      ["employeeName", "Employee name", ""],
      ["jobTitle", "New job title", ""],
      ["lineManager", "Line manager", ""],
      ["effectiveDate", "Effective date", ""],
    ]);
  }

  if (documentType === "Training Certificate") {
    return metadata([
      ["employeeName", "Employee name", lowerName.includes("simon") || lowerSubject.includes("simon") ? "Simon Penrose" : ""],
      ["courseName", "Course name", ""],
      ["completionDate", "Completion date", ""],
      ["provider", "Training provider", "", false],
    ]);
  }

  return metadata([
    ["employeeName", "Employee name", ""],
    ["reference", "Reference", "", false],
  ]);
}

function createCandidate(email: EmailInfo, kind: CandidateKind, name: string, index: number): DocumentCandidate {
  const id = `${email.id}-${kind}-${index}`;
  const defaultSelection = kind === "attachment" || email.attachments.length === 0;
  const lowerName = normalize(name);
  const lowerSubject = normalize(email.subject);

  if (lowerName.includes("felix dunmar cv") || lowerSubject.includes("candidate cv - felix dunmar")) {
    return {
      id,
      emailId: email.id,
      emailSubject: email.subject,
      kind,
      name,
      selected: defaultSelection,
      status: "ready",
      documentType: "Candidate CV",
      suggestions: ["Candidate CV", "Offer Letter", "Employment Contract"],
      metadata: metadata([
        ["firstName", "First name", "Felix"],
        ["lastName", "Last name", "Dunmar"],
        ["email", "Email address", "felix.dunmar@kessellix.co.uk"],
        ["role", "Role applied for", "HR Coordinator"],
        ["dob", "Date of birth", "1993-05-14"],
      ]),
      note: "Ready to submit",
    };
  }

  if (lowerSubject.includes("sickness absence note - cara linton")) {
    return {
      id,
      emailId: email.id,
      emailSubject: email.subject,
      kind,
      name: "Email message",
      selected: true,
      status: "needs-review",
      documentType: "Absence Note",
      suggestions: ["Absence Note", "Performance Review", "Change of Role"],
      metadata: metadata([
        ["employeeName", "Employee name", "Cara Linton"],
        ["absenceType", "Absence type", "Sickness"],
        ["startDate", "Start date", "2026-06-29"],
        ["endDate", "Expected return date", "", false],
        ["reason", "Reason", "Flu symptoms", false],
      ]),
      note: "Optional return date not found",
    };
  }

  if (lowerSubject.includes("please get back in touch")) {
    return {
      id,
      emailId: email.id,
      emailSubject: email.subject,
      kind,
      name: "Email message",
      selected: true,
      status: "needs-review",
      documentType: "",
      suggestions: ["Absence Note", "Change of Role", "Performance Review"],
      metadata: metadata([]),
      note: "Document Care could not identify this document type. Choose one to continue.",
    };
  }

  if (lowerName.includes("offer letter") || lowerSubject.includes("new starter documents - simon penrose")) {
    return {
      id,
      emailId: email.id,
      emailSubject: email.subject,
      kind,
      name,
      selected: defaultSelection,
      status: "needs-review",
      documentType: "Offer Letter",
      suggestions: ["Offer Letter", "Employment Contract", "Candidate CV"],
      metadata: metadata([
        ["candidateName", "Candidate name", "Simon Penrose"],
        ["role", "Role offered", "People Operations Advisor"],
        ["salary", "Salary", "£38,000"],
        ["startDate", "Proposed start date", "2026-07-15"],
        ["hiringManager", "Hiring manager", ""],
      ]),
      note: "Hiring manager needs review",
    };
  }

  if (lowerName.includes("right to work") || lowerSubject.includes("right to work simon penrose")) {
    return {
      id,
      emailId: email.id,
      emailSubject: email.subject,
      kind,
      name,
      selected: defaultSelection,
      status: "needs-review",
      documentType: "Right to Work Evidence",
      suggestions: ["Right to Work Evidence", "Training Certificate", "Candidate CV"],
      metadata: metadata([
        ["firstName", "First name", "Simon"],
        ["lastName", "Last name", "Penrose"],
        ["documentType", "Document type", "Passport"],
        ["expiryDate", "Expiry date", "2031-06-12"],
        ["verificationDate", "Verification date", ""],
      ]),
      note: "Verification date needs review",
    };
  }

  if (lowerName.includes("scanned document")) {
    return {
      id,
      emailId: email.id,
      emailSubject: email.subject,
      kind,
      name,
      selected: true,
      status: "needs-review",
      documentType: "",
      suggestions: ["Right to Work Evidence", "Offer Letter", "Training Certificate"],
      metadata: metadata([]),
      note: "Document Care could not identify this document type. Choose one to continue.",
    };
  }

  return {
    id,
    emailId: email.id,
    emailSubject: email.subject,
    kind,
    name,
    selected: defaultSelection,
    status: "needs-review",
    documentType: "",
    suggestions: ["Candidate CV", "Absence Note", "Offer Letter"],
    metadata: metadata([
      ["employeeName", "Employee name", ""],
      ["reference", "Reference", "", false],
    ]),
    note: "Demo content not recognised",
  };
}

function createCandidates(emails: EmailInfo[]): DocumentCandidate[] {
  return emails.flatMap((email) => {
    const emailCandidate = createCandidate(email, "email", "Email message", 0);
    const attachmentCandidates = email.attachments.map((attachment, index) =>
      createCandidate(email, "attachment", attachment.name, index + 1)
    );

    return [emailCandidate, ...attachmentCandidates];
  });
}

function isCandidateReady(candidate: DocumentCandidate): boolean {
  return (
    !candidate.selected ||
    (candidate.documentType.length > 0 && candidate.metadata.every((field) => !field.required || field.value.trim().length > 0))
  );
}

function getRequiredMissingCount(candidate: DocumentCandidate): number {
  return candidate.metadata.filter((field) => field.required && !field.value.trim()).length;
}

function candidateStatus(candidate: DocumentCandidate): CandidateStatus {
  if (!candidate.selected) {
    return "not-selected";
  }

  return isCandidateReady(candidate) ? "ready" : "needs-review";
}

function nextCandidateIndex(candidates: DocumentCandidate[], currentIndex: number): number {
  const next = candidates.findIndex((candidate, index) => index > currentIndex && candidate.selected);
  if (next >= 0) {
    return next;
  }

  const first = candidates.findIndex((candidate) => candidate.selected);
  return first >= 0 ? first : 0;
}

function closeTaskPane() {
  if (typeof Office !== "undefined") {
    Office.context.ui.closeContainer?.();
  }
}

const App: React.FC = () => {
  const [stage, setStage] = React.useState<Stage>("loading");
  const [emails, setEmails] = React.useState<EmailInfo[]>([]);
  const [candidates, setCandidates] = React.useState<DocumentCandidate[]>([]);
  const [activeIndex, setActiveIndex] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;

    async function initialise() {
      const selectedEmails = await getSelectedEmails();
      const generatedCandidates = createCandidates(selectedEmails);

      window.setTimeout(() => {
        if (cancelled) {
          return;
        }

        setEmails(selectedEmails);
        setCandidates(generatedCandidates);
        setActiveIndex(generatedCandidates.findIndex((candidate) => candidate.selected) || 0);
        setStage("selecting");
      }, 3000);
    }

    initialise();

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedCandidates = candidates.filter((candidate) => candidate.selected);
  const readyCount = selectedCandidates.filter(isCandidateReady).length;
  const needsReviewCount = selectedCandidates.length - readyCount;
  const activeCandidate = candidates[activeIndex];
  const activeSelectedPosition = activeCandidate
    ? selectedCandidates.findIndex((candidate) => candidate.id === activeCandidate.id)
    : -1;
  const canSubmit = selectedCandidates.length > 0 && needsReviewCount === 0;

  function updateCandidate(candidateId: string, update: (candidate: DocumentCandidate) => DocumentCandidate) {
    setCandidates((current) =>
      current.map((candidate) => {
        if (candidate.id !== candidateId) {
          return candidate;
        }

        const updated = update(candidate);
        return { ...updated, status: candidateStatus(updated) };
      })
    );
  }

  function toggleCandidate(candidateId: string) {
    updateCandidate(candidateId, (candidate) => ({ ...candidate, selected: !candidate.selected }));
  }

  function identifyDocumentTypes() {
    setStage("analysing");
    window.setTimeout(() => {
      setStage("reviewing");
      setActiveIndex(nextCandidateIndex(candidates, -1));
    }, 3000);
  }

  function setDocumentType(candidateId: string, documentType: string) {
    updateCandidate(candidateId, (candidate) => ({
      ...candidate,
      documentType,
      metadata: metadataForDocumentType(documentType, candidate),
      note: documentType === "Right to Work Evidence" ? "Verification date needs review" : candidate.note,
    }));
  }

  function updateMetadata(candidateId: string, key: string, value: string) {
    updateCandidate(candidateId, (candidate) => ({
      ...candidate,
      metadata: candidate.metadata.map((field) => (field.key === key ? { ...field, value } : field)),
    }));
  }

  function setActiveSelectedPosition(position: number) {
    const candidate = selectedCandidates[position];

    if (!candidate) {
      return;
    }

    setActiveIndex(candidates.findIndex((item) => item.id === candidate.id));
  }

  function backMetadataReview() {
    if (activeSelectedPosition <= 0) {
      setStage("selecting");
      return;
    }

    setActiveSelectedPosition(activeSelectedPosition - 1);
  }

  function continueMetadataReview() {
    if (!activeCandidate || !isCandidateReady(activeCandidate)) {
      return;
    }

    if (activeSelectedPosition < selectedCandidates.length - 1) {
      setActiveSelectedPosition(activeSelectedPosition + 1);
      return;
    }

    setStage("ready-to-submit");
  }

  function submitBatch() {
    setStage("submitting");
    window.setTimeout(() => {
      setCandidates((current) =>
        current.map((candidate) => (candidate.selected ? { ...candidate, status: "submitted" } : candidate))
      );
      setStage("submitted");
    }, 900);
  }

  if (stage === "loading") {
    return (
      <main className="min-h-screen bg-dc-background text-dc-text">
        <Header title="Review documents" />
        <div className="p-4">
          <section className="rounded-dc-md border border-dc-border bg-dc-surface p-4">
            <div className="flex items-center gap-3">
              <span className="dc-spinner" />
              <div>
                <p className="text-sm font-semibold">Reading Outlook selection</p>
                <p className="text-xs text-dc-textMuted">Preparing emails and attachments for review.</p>
              </div>
            </div>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-dc-background text-dc-text">
      <Header title={stage === "submitted" ? "Documents submitted" : "Review documents"} />
      <StepIndicator stage={stage} />

      <div className="flex min-h-[calc(100vh-107px)] flex-col gap-3 p-3">
        {stage === "selecting" && (
          <>
            <SelectionChecklist emails={emails} candidates={candidates} onToggle={toggleCandidate} />
            <button
              className="dc-primary-button w-full"
              type="button"
              onClick={identifyDocumentTypes}
              disabled={!selectedCandidates.length}
            >
              Identify documents
            </button>
          </>
        )}

        {stage === "analysing" && (
          <section className="rounded-dc-md border border-dc-border bg-dc-surface p-4">
            <div className="flex items-center gap-3">
              <span className="dc-spinner" />
              <div>
                <p className="text-sm font-semibold">Identifying document types</p>
                <p className="text-xs text-dc-textMuted">Reading selected documents and preparing metadata.</p>
              </div>
            </div>
          </section>
        )}

        {stage === "reviewing" && activeCandidate && (
          <>
            <ReviewCard
              candidate={activeCandidate}
              candidateIndex={activeSelectedPosition}
              candidateCount={selectedCandidates.length}
              onChooseType={(type) => setDocumentType(activeCandidate.id, type)}
              onUpdateMetadata={(key, value) => updateMetadata(activeCandidate.id, key, value)}
            />
            <div className="grid grid-cols-2 gap-2">
              <button className="dc-secondary-button" type="button" onClick={backMetadataReview}>
                Back
              </button>
              <button className="dc-primary-button" type="button" onClick={continueMetadataReview} disabled={!isCandidateReady(activeCandidate)}>
                {activeSelectedPosition < selectedCandidates.length - 1 ? "Save and next" : "Review submission"}
              </button>
            </div>
            {!isCandidateReady(activeCandidate) && (
              <p className="text-center text-xs text-dc-textMuted">
                Complete required metadata before continuing.
              </p>
            )}
          </>
        )}

        {stage === "ready-to-submit" && (
          <>
            <SubmissionSummary candidates={selectedCandidates} />
            <div className="grid grid-cols-2 gap-2">
              <button className="dc-secondary-button" type="button" onClick={() => setStage("reviewing")}>
                Back
              </button>
              <button className="dc-primary-button" type="button" onClick={submitBatch} disabled={!canSubmit}>
                Submit document{selectedCandidates.length === 1 ? "" : "s"}
              </button>
            </div>
            {!canSubmit && (
              <p className="text-center text-xs text-dc-textMuted">
                Resolve {needsReviewCount} document{needsReviewCount === 1 ? "" : "s"} before submitting.
              </p>
            )}
          </>
        )}

        {stage === "submitting" && (
          <section className="rounded-dc-md border border-dc-border bg-dc-surface p-4">
            <div className="flex items-center gap-3">
              <span className="dc-spinner" />
              <div>
                <p className="text-sm font-semibold">Submitting documents</p>
                <p className="text-xs text-dc-textMuted">Creating the Document Care batch.</p>
              </div>
            </div>
          </section>
        )}

        {stage === "submitted" && (
          <SuccessCard candidates={selectedCandidates} onClose={closeTaskPane} />
        )}
      </div>
    </main>
  );
};

function Header({ title }: { title: string }) {
  return (
    <div className="border-b border-dc-border bg-dc-surface px-4 py-3">
      <p className="text-xs font-semibold uppercase text-dc-primary">TSG Document Care</p>
      <h1 className="text-lg font-semibold">{title}</h1>
    </div>
  );
}

function StepIndicator({ stage }: { stage: Stage }) {
  const metadataActive = stage === "reviewing" || stage === "ready-to-submit" || stage === "submitting" || stage === "submitted";
  const documentTypeClass = metadataActive
    ? "bg-dc-background text-dc-textMuted"
    : "bg-dc-primarySubtle text-dc-primary";
  const metadataClass = metadataActive
    ? "bg-dc-primarySubtle text-dc-primary"
    : "bg-dc-background text-dc-textMuted";
  const chevronColor = metadataActive ? "rgb(var(--dc-color-background))" : "rgb(var(--dc-color-primary-subtle))";

  return (
    <nav className="border-b border-dc-border bg-dc-surface text-xs font-semibold" aria-label="Document Care progress">
      <ol className="grid grid-cols-[1fr_18px_1fr]">
        <li className={`truncate px-4 py-2.5 ${documentTypeClass}`}>
          Document types
        </li>
        <li className={`relative ${metadataClass}`} aria-hidden="true">
          <span
            className="absolute left-0 top-0 h-0 w-0"
            style={{
              borderTop: "17px solid transparent",
              borderBottom: "17px solid transparent",
              borderLeft: `18px solid ${chevronColor}`,
            }}
          />
        </li>
        <li className={`truncate px-4 py-2.5 ${metadataClass}`}>Document metadata</li>
      </ol>
    </nav>
  );
}

function SelectionChecklist({
  emails,
  candidates,
  onToggle,
}: {
  emails: EmailInfo[];
  candidates: DocumentCandidate[];
  onToggle: (candidateId: string) => void;
}) {
  return (
    <div className="space-y-3">
      {emails.map((email) => {
        const emailCandidates = candidates.filter((candidate) => candidate.emailId === email.id);
        const emailCandidate = emailCandidates.find((candidate) => candidate.kind === "email");
        const attachments = emailCandidates.filter((candidate) => candidate.kind === "attachment");

        return (
          <section className="overflow-hidden rounded-dc-md border border-dc-border bg-dc-surface shadow-dc-sm" key={email.id}>
            <div className="border-b border-dc-border bg-dc-surfaceMuted px-3 py-3">
              <p className="truncate text-sm font-semibold" title={email.subject}>
                {email.subject}
              </p>
            </div>
            <div className="divide-y divide-dc-border">
              {emailCandidate && (
                <button
                  className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left"
                  type="button"
                  onClick={() => onToggle(emailCandidate.id)}
                  title={email.subject}
                >
                  <span className="min-w-0 truncate text-sm font-semibold">This email</span>
                  <Checkbox selected={emailCandidate.selected} />
                </button>
              )}
              {attachments.map((candidate) => (
                <button
                  className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left"
                  key={candidate.id}
                  type="button"
                  onClick={() => onToggle(candidate.id)}
                  title={candidate.name}
                >
                  <span className="min-w-0 truncate text-sm font-semibold">{candidate.name}</span>
                  <Checkbox selected={candidate.selected} />
                </button>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function Checkbox({ selected }: { selected: boolean }) {
  return (
    <input
      checked={selected}
      className="pointer-events-none h-5 w-5 shrink-0 accent-dc-primary"
      readOnly
      tabIndex={-1}
      type="checkbox"
    />
  );
}

function ReviewCard({
  candidate,
  candidateIndex,
  candidateCount,
  onChooseType,
  onUpdateMetadata,
}: {
  candidate: DocumentCandidate;
  candidateIndex: number;
  candidateCount: number;
  onChooseType: (documentType: string) => void;
  onUpdateMetadata: (key: string, value: string) => void;
}) {
  return (
    <section className="rounded-dc-md border border-dc-border bg-dc-surface shadow-dc-sm">
      <div className="border-b border-dc-border bg-dc-background/70 px-3 py-3">
        <p className="text-xs font-semibold uppercase text-dc-textMuted">
          Document {candidateIndex + 1} of {candidateCount}
        </p>
        <p className="mt-1 truncate text-sm font-semibold" title={candidate.name}>
          {candidate.name}
        </p>
        <p className="mt-1 line-clamp-2 text-xs text-dc-textMuted">{candidate.emailSubject}</p>
      </div>

      <div className="p-3">
        <div>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase text-dc-textMuted">Document type</span>
            <select
              className="w-full rounded-dc-sm border border-dc-border bg-dc-background px-3 py-2 text-sm outline-none focus:border-dc-primary"
              value={candidate.documentType}
              onChange={(event) => onChooseType(event.target.value)}
            >
              <option value="">Select document type</option>
              {documentTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase text-dc-textMuted">Document metadata</p>
          <div className="space-y-2">
            {candidate.metadata.map((field) => (
              <label className="block" key={field.key}>
                <span className="mb-1 flex items-center justify-between text-xs font-semibold text-dc-textMuted">
                  {field.label}
                  {!field.required && <span className="font-normal">Optional</span>}
                </span>
                <input
                  className={
                    field.required && !field.value.trim()
                      ? "w-full rounded-dc-sm border border-dc-warning bg-dc-background px-3 py-2 text-sm outline-none"
                      : "w-full rounded-dc-sm border border-dc-border bg-dc-background px-3 py-2 text-sm outline-none focus:border-dc-primary"
                  }
                  type={isDateField(field) ? "date" : "text"}
                  value={field.value}
                  onChange={(event) => onUpdateMetadata(field.key, event.target.value)}
                />
              </label>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}

function SubmissionSummary({ candidates }: { candidates: DocumentCandidate[] }) {
  return (
    <section className="rounded-dc-md border border-dc-border bg-dc-surface shadow-dc-sm">
      <div className="border-b border-dc-border px-3 py-3">
        <p className="text-sm font-semibold">Ready to submit</p>
        <p className="mt-1 text-xs text-dc-textMuted">
          {candidates.length} document{candidates.length === 1 ? "" : "s"} have verified metadata.
        </p>
      </div>
      <div className="divide-y divide-dc-border">
        {candidates.map((candidate) => (
          <div className="px-3 py-3" key={candidate.id}>
            <p className="truncate text-sm font-semibold" title={candidate.name}>
              {candidate.name}
            </p>
            <p className="mt-1 truncate text-xs text-dc-textMuted" title={candidate.documentType}>
              {candidate.documentType}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function SuccessCard({ candidates, onClose }: { candidates: DocumentCandidate[]; onClose: () => void }) {
  return (
    <section className="rounded-dc-md border border-dc-success/30 bg-dc-surface p-4 shadow-dc-sm">
      <p className="text-sm font-semibold text-dc-success">Submitted to Document Care</p>
      <p className="mt-2 text-sm text-dc-textMuted">
        Your document batch has been created.
      </p>
      <div className="mt-3 space-y-2">
        {candidates.map((candidate) => (
          <div className="rounded-dc-sm border border-dc-border bg-dc-background p-2" key={candidate.id}>
            <p className="text-sm font-semibold">{candidate.name}</p>
            <p className="text-xs text-dc-textMuted">{candidate.documentType}</p>
          </div>
        ))}
      </div>
      <button className="dc-primary-button mt-4 w-full" type="button">
        View in Document Care
      </button>
      <button className="dc-secondary-button mt-2 w-full" type="button" onClick={onClose}>
        Close
      </button>
    </section>
  );
}

function candidateStatusLabel(candidate: DocumentCandidate): string {
  if (!candidate.selected) {
    return "Not selected";
  }

  if (candidate.status === "submitted") {
    return "Submitted";
  }

  const missingCount = getRequiredMissingCount(candidate);

  if (!candidate.documentType) {
    return "Needs type";
  }

  if (missingCount > 0) {
    return `${missingCount} missing`;
  }

  return "Ready";
}

export default App;
